import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';

import { useCollectionSurtitle } from '../../hooks/useSurtitle';
import { useCollectionUrlGenerator } from '../../hooks/useUrlGenerator';
// ...existing code...
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import CollectionImage from '../partials/CollectionImage';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import CardTitle from '../typography/CardTitle';
import HorizontalCard from './HorizontalCard';

import styles from '../../styles/cards/collection-horizontal-card.module.css';

export interface CollectionHorizontalCardProps extends Partial<Omit<CollectionSnippet, 'theme'>> {
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize | null;
    ratio?: CardRatio | null;
    height?: number | null;
    placeholder?: boolean;
    withBorder?: boolean;
    withBrandIcon?: boolean;
    withoutSerieFormat?: boolean;
    withoutCategory?: boolean;
    withFormat?: boolean;
    withoutSurtitle?: boolean;
    className?: string | null;
}

function CollectionHorizontalCard({
    type = null,
    slug = null,
    surtitle = null,
    title = null,
    description = null,
    image = null,
    brands = null,
    sponsors = null,
    itemsCount = null,
    childrenCount = null,
    issueNumber = null,
    serieFormat = null,
    categories,
    format = null,
    topic = null,
    topics = null,
    formats = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    height = null,
    placeholder = false,
    withBorder = false,
    withBrandIcon = false,
    withoutSerieFormat = false,
    withoutCategory = false,
    withoutSurtitle = false,
    withFormat = false,
    className = null,
}: CollectionHorizontalCardProps) {
    const theme = normalizeCardTheme(providedTheme);
    const collectionUrl = useCollectionUrlGenerator();
    const [brand = null] = brands || [];
    const { handle: brandHandle } = brand || {};
    const thumbnailRatio = getRatio(ratio, 5 / 4);
    const [sponsor = null] = sponsors || [];

    const finalSurtitle = useCollectionSurtitle({
        type,
        surtitle,
        itemsCount,
        childrenCount,
        issueNumber,
        serieFormat,
        categories,
        topics: topics || (topic !== null ? [topic] : null),
        formats: formats || (format !== null ? [format] : null),
        withCategory: !withoutCategory,
        withSerieFormat: !withoutSerieFormat,
        withFormat,
    });

    const isBig = size === 'big';
    const isSmall = size === 'small';
    const isTiny = size === 'tiny';

    let defaultHeight = 100;
    if (isBig) {
        defaultHeight = 150;
    } else if (isSmall) {
        defaultHeight = 70;
    } else if (isTiny) {
        defaultHeight = 50;
    }

    const finalHeight = height || defaultHeight;

    return (
        <HorizontalCard
            theme={theme || 'none'}
            href={
                collectionUrl({
                    type,
                    slug,
                }) || '#'
            }
            thumbnail={
                <CollectionImage
                    brand={brandHandle}
                    width={thumbnailRatio * finalHeight}
                    height={finalHeight}
                    media={image}
                    withPlaceholder={placeholder}
                    withBrandIcon={withBrandIcon}
                    className={styles.thumbnail}
                />
            }
            thumbnailRatio={thumbnailRatio}
            height={finalHeight}
            labelClassName={styles.label}
            className={classNames([
                styles.container,
                {
                    [styles.withBorder]: withBorder,
                },
                styles[camelCase(size)],
                className,
            ])}
        >
            {!withoutSurtitle ? (
                <Surtitle surtitle={finalSurtitle} className={styles.surtitle} />
            ) : null}
            <CardTitle tag="span" className={styles.title} placeholder={placeholder}>
                {title}
            </CardTitle>

            {/* <CardDescriptionTypography
                tag="span"
                className={styles.description}
                placeholder={placeholder}
                html={description}
            /> */}

            {sponsor !== null ? <Sponsor {...sponsor} className={styles.sponsor} /> : null}
        </HorizontalCard>
    );
}

export default CollectionHorizontalCard;
